import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Github, Shield, Cloud, Lock, Zap, Wifi, Upload } from 'lucide-react';
import './SoulProject.css';

function LanProject() {
    const navigate = useNavigate();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            icon: <Cloud size={32} />,
            title: "Public File Sharing",
            desc: "Upload files accessible to anyone on the network with real-time updates"
        },
        {
            icon: <Lock size={32} />,
            title: "Private Room-Based Sharing",
            desc: "Create secure private rooms with unique 9-digit IDs for controlled access"
        },
        {
            icon: <Zap size={32} />,
            title: "Real-Time Updates",
            desc: "WebSocket-based live file synchronization and upload progress tracking"
        },
        {
            icon: <Shield size={32} />,
            title: "AES-256 Encryption",
            desc: "Secure user authentication with AES-256 password encryption"
        },
        {
            icon: <Wifi size={32} />,
            title: "LAN-Based Architecture",
            desc: "High-speed file sharing over local network without internet dependency"
        },
        {
            icon: <Upload size={32} />,
            title: "Large File Support",
            desc: "Support for files up to 100MB with upload progress and speed indicators"
        }
    ];

    const techStack = [
        { category: "Backend", items: ["Node.js", "Express.js", "Socket.IO"] },
        { category: "Database", items: ["MySQL", "Session Management"] },
        { category: "Security", items: ["AES-256", "HTTPS/SSL", "CORS Protection"] },
        { category: "Frontend", items: ["Vanilla JavaScript", "WebSocket Client", "Responsive UI"] }
    ];

    const systemFeatures = [
        {
            title: "Public File Sharing",
            items: [
                "Upload files accessible to anyone on the network",
                "View, download, and delete public files",
                "Real-time file list updates",
                "Upload progress tracking with speed indicator",
                "Support for files up to 100MB"
            ]
        },
        {
            title: "Private Room-Based Sharing",
            items: [
                "Create private rooms with unique 9-digit IDs",
                "Share room IDs for secure file access",
                "Real-time file synchronization via WebSocket",
                "Automatic room cleanup when all users leave",
                "Cross-device room access"
            ]
        },
        {
            title: "User Authentication",
            items: [
                "Secure user registration and login",
                "AES-256 encryption for passwords",
                "Session management",
                "User profile management"
            ]
        },
        {
            title: "Security Features",
            items: [
                "HTTPS support with SSL certificates",
                "AES-256 password encryption",
                "CORS protection",
                "File type validation",
                "Secure file storage"
            ]
        }
    ];

    return (
        <div className="soul-project">
            {/* Header */}
            <header className="project-header">
                <button onClick={() => navigate('/#projects')} className="back-btn">
                    <ArrowLeft size={20} /> Back to Portfolio
                </button>

                <div className="project-hero">
                    <span className="project-tag">Mini Project</span>
                    <h1>LAN File Sharing</h1>
                    <h2>Secure Online Upload & Link System</h2>
                    <p className="tagline">
                        A comprehensive LAN-based file-sharing system featuring both public and private
                        room-based sharing with real-time WebSocket updates, AES-256 encryption, and
                        SSL/TLS security for high-speed local network file transfers.
                    </p>
                </div>
            </header>

            {/* Overview Section */}
            <section className="section">
                <h3>Project Overview</h3>
                <div className="overview-content">
                    <p>
                        The LAN-Based File Sharing System (S.O.U.L - Secure Online Upload & Link) is a
                        robust solution for sharing files over local networks without internet dependency.
                        Built with Node.js, Express, and Socket.IO, it provides both public and private
                        file-sharing capabilities with enterprise-grade security.
                    </p>
                    <p>
                        The system supports real-time file synchronization through WebSocket connections,
                        allowing multiple users to collaborate seamlessly. With AES-256 encryption for
                        authentication and HTTPS/SSL for secure communication, it ensures data privacy
                        while maintaining high-speed transfer rates over LAN.
                    </p>
                </div>
            </section>

            {/* Features Section */}
            <section className="section">
                <h3>Key Features</h3>
                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <div className="feature-icon">{feature.icon}</div>
                            <h4>{feature.title}</h4>
                            <p>{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tech Stack Section */}
            <section className="section">
                <h3>Technology Stack</h3>
                <div className="tech-grid">
                    {techStack.map((tech, index) => (
                        <div key={index} className="tech-category">
                            <h4>{tech.category}</h4>
                            <div className="tech-items">
                                {tech.items.map((item, i) => (
                                    <span key={i} className="tech-badge">{item}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* System Features */}
            <section className="section">
                <h3>System Features</h3>
                <div className="problem-solution">
                    {systemFeatures.map((feature, index) => (
                        <div key={index} className="ps-card solution">
                            <h4>{feature.title}</h4>
                            <ul>
                                {feature.items.map((item, i) => (
                                    <li key={i}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* System Architecture */}
            <section className="section">
                <h3>System Architecture</h3>
                <div className="overview-content">
                    <h4 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Client-Server Model</h4>
                    <p>
                        The system follows a client-server architecture where the Express server handles
                        HTTP/HTTPS requests and WebSocket connections. Clients connect via web browsers
                        on the same local network.
                    </p>
                    <br />
                    <h4 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Components:</h4>
                    <ul style={{ color: 'var(--text-secondary)', lineHeight: '2', marginLeft: '2rem' }}>
                        <li><strong>Express Server:</strong> Handles authentication, file operations, and room management</li>
                        <li><strong>Socket.IO:</strong> Provides real-time bidirectional communication</li>
                        <li><strong>MySQL Database:</strong> Stores user credentials and session data</li>
                        <li><strong>File System:</strong> Organized storage for public and private files</li>
                        <li><strong>Multer Middleware:</strong> Handles multipart/form-data file uploads</li>
                    </ul>
                </div>
            </section>

            {/* How It Works */}
            <section className="section">
                <h3>How It Works</h3>
                <div className="workflow">
                    <div className="workflow-step">
                        <div className="step-number">1</div>
                        <h4>User Registration</h4>
                        <p>Create an account with AES-256 encrypted password storage in MySQL database</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">2</div>
                        <h4>Choose Sharing Mode</h4>
                        <p>Select between public file sharing or create/join a private room with unique ID</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">3</div>
                        <h4>Upload Files</h4>
                        <p>Upload files up to 100MB with real-time progress tracking and speed indicators</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">4</div>
                        <h4>Real-Time Sync</h4>
                        <p>WebSocket automatically updates file lists for all connected users in real-time</p>
                    </div>
                </div>
            </section>

            {/* Technical Highlights */}
            <section className="section">
                <h3>Technical Highlights</h3>
                <div className="problem-solution">
                    <div className="ps-card problem">
                        <h4>Security Implementation</h4>
                        <ul>
                            <li>AES-256 encryption for password storage</li>
                            <li>HTTPS/SSL for encrypted communication</li>
                            <li>Session-based authentication</li>
                            <li>CORS protection against unauthorized access</li>
                            <li>File type validation and size limits</li>
                            <li>Secure file path handling</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Performance Features</h4>
                        <ul>
                            <li>High-speed LAN transfers (no internet required)</li>
                            <li>WebSocket for minimal latency updates</li>
                            <li>Efficient file streaming with Multer</li>
                            <li>Automatic room cleanup and memory management</li>
                            <li>Optimized database queries</li>
                            <li>Concurrent user support</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="section">
                <h3>Use Cases</h3>
                <div className="overview-content">
                    <ul style={{ color: 'var(--text-secondary)', lineHeight: '2', marginLeft: '2rem' }}>
                        <li><strong>Office Environments:</strong> Share documents and files within the organization without cloud dependency</li>
                        <li><strong>Educational Institutions:</strong> Distribute course materials and assignments to students on campus network</li>
                        <li><strong>Home Networks:</strong> Share media files between family members' devices</li>
                        <li><strong>Development Teams:</strong> Quick file sharing during collaborative sessions</li>
                        <li><strong>Secure Transfers:</strong> Share sensitive files without uploading to external servers</li>
                    </ul>
                </div>
            </section>

            {/* Project Links */}
            <section className="section">
                <div className="project-links">
                    <a href="https://github.com/ANJOEJAMES" target="_blank" rel="noreferrer" className="project-btn">
                        <Github size={20} /> View on GitHub
                    </a>
                    <button onClick={() => navigate('/#projects')} className="project-btn secondary">
                        <ArrowLeft size={20} /> Back to Portfolio
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="project-footer">
                <p>© {new Date().getFullYear()} Anjoe James. LAN File Sharing Project.</p>
            </footer>
        </div>
    );
}

export default LanProject;
